import { ChakraV3, FloatingPopover, FloatingPopoverBody } from '@piccolohealth/ui';
import React from 'react';
import { useFastForwardParticipantMutation } from '../../graphql/hooks/useFastForwardParticipantMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { usePermission } from '../../hooks/usePermission';

export interface Props {
  participantId: string;
}

export const ParticipantVersionStatus = (props: Props) => {
  const { participantId } = props;

  const { organization, successToast, errorToast } = useAppContext();
  const disclosure = ChakraV3.useDisclosure();
  const fastForwardParticipantMutation = useFastForwardParticipantMutation();
  const hasPermission = usePermission('fastForward', 'participant');

  const onFastForward = React.useCallback(async () => {
    await fastForwardParticipantMutation
      .mutateAsync({
        organizationId: organization.id,
        participantId: participantId,
      })
      .then(() => {
        successToast('Participant updated successfully');
        disclosure.onClose();
      })
      .catch((err) => {
        errorToast(`Error updating participant: ${err.message}`);
      });
  }, [
    fastForwardParticipantMutation,
    organization.id,
    participantId,
    successToast,
    errorToast,
    disclosure,
  ]);

  return (
    <ChakraV3.Alert.Root status='info' variant='surface' alignItems='center'>
      <ChakraV3.Alert.Indicator />
      <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
        This participant is using fields from an older version because it was archived. If you would
        like to use the latest version{' '}
        <FloatingPopover
          open={disclosure.open}
          setOpen={disclosure.setOpen}
          placement='right'
          render={({ context }) => (
            <FloatingPopoverBody title='Confirmation' w='sm' color='initial'>
              <ChakraV3.Box>
                Updating to the latest version may result in data loss if fields are not compatible
                or deleted. Are you sure you want to continue?
              </ChakraV3.Box>

              <ChakraV3.Separator />

              <ChakraV3.HStack alignSelf='end'>
                <ChakraV3.Button
                  size='xs'
                  variant='subtle'
                  onClick={() => context.onOpenChange(false)}
                >
                  Cancel
                </ChakraV3.Button>
                <ChakraV3.Button
                  colorPalette='red'
                  size='xs'
                  onClick={onFastForward}
                  loading={fastForwardParticipantMutation.isLoading}
                >
                  Apply
                </ChakraV3.Button>
              </ChakraV3.HStack>
            </FloatingPopoverBody>
          )}
        >
          <ChakraV3.Span>
            <ChakraV3.Tooltip
              content='Only admin users can update a participant to the latest fields'
              disabled={hasPermission}
            >
              <ChakraV3.Button
                size='sm'
                color='purple.600'
                onClick={disclosure.onOpen}
                disabled={!hasPermission}
                fontWeight='semibold'
                verticalAlign='baseline'
                variant='plain'
              >
                click here.
              </ChakraV3.Button>
            </ChakraV3.Tooltip>
          </ChakraV3.Span>
        </FloatingPopover>
      </ChakraV3.Text>
    </ChakraV3.Alert.Root>
  );
};
